export interface ShareCardOptions {
  zoom0ImageUrl: string;
  puzzleNumber: number;
  isArchive: boolean;
  locale: string;
}

const CARD_W = 1200;
const CARD_H = 630;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = url;
  });
}

function shareMessage(opts: ShareCardOptions): string {
  if (opts.locale === 'en') {
    return opts.isArchive
      ? `Come replay archive #${opts.puzzleNumber} on Dezoom!`
      : 'Come challenge me on today\'s Dezoom!';
  }
  return opts.isArchive
    ? `Viens rejouer l'archive #${opts.puzzleNumber} sur Dezoom !`
    : 'Viens me défier sur le Dezoom du jour !';
}

export function getShareUrl(puzzleNumber: number, isArchive: boolean): string {
  const base = 'https://dezoom.eu';
  return isArchive
    ? `${base}/archives/${puzzleNumber}/play`
    : `${base}/play`;
}

export function getShareText(opts: ShareCardOptions): string {
  const msg = shareMessage(opts);
  return `${msg}\n${getShareUrl(opts.puzzleNumber, opts.isArchive)}`;
}

export async function buildShareCardBlob(opts: ShareCardOptions): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas_unavailable');

  ctx.fillStyle = '#1a1730';
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  const imgAreaH = CARD_H - 140;

  try {
    const img = await loadImage(opts.zoom0ImageUrl);
    const scale = Math.max(CARD_W / img.width, imgAreaH / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    const x = (CARD_W - w) / 2;
    const y = (imgAreaH - h) / 2;
    ctx.drawImage(img, x, y, w, h);
  } catch {
    ctx.fillStyle = '#2d2640';
    ctx.fillRect(0, 0, CARD_W, imgAreaH);
  }

  const gradient = ctx.createLinearGradient(0, imgAreaH - 80, 0, imgAreaH);
  gradient.addColorStop(0, 'rgba(26, 23, 48, 0)');
  gradient.addColorStop(1, 'rgba(26, 23, 48, 1)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, imgAreaH - 80, CARD_W, 80);

  ctx.fillStyle = '#1a1730';
  ctx.fillRect(0, imgAreaH, CARD_W, CARD_H - imgAreaH);

  ctx.fillStyle = '#8b5cf6';
  ctx.font = 'bold 36px Outfit, system-ui, sans-serif';
  ctx.fillText('Dezoom', 48, imgAreaH + 52);

  ctx.fillStyle = '#f8fafc';
  ctx.font = '600 32px Outfit, system-ui, sans-serif';
  const msg = shareMessage(opts);
  ctx.fillText(msg, 48, imgAreaH + 100, CARD_W - 96);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('blob_failed'));
    }, 'image/png');
  });
}

export async function downloadShareCard(opts: ShareCardOptions, filename?: string): Promise<void> {
  const blob = await buildShareCardBlob(opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `dezoom-${opts.puzzleNumber}.png`;
  a.click();
  URL.revokeObjectURL(url);
}
